import { del, generateClient } from "@aws-amplify/api";
import {
  fetchMFAPreference,
  fetchUserAttributes,
  fetchAuthSession,
  AuthSession,
  FetchMFAPreferenceOutput,
  getCurrentUser,
} from "aws-amplify/auth";
import { signOutAndDeleteCookies } from "@blings/shared-auth-component";
import { flow, getRoot, Instance, types, cast } from "mobx-state-tree";
import { insertIntoGraphQlString } from "modifygraphqlstring";
import { NavigateFunction } from "react-router";
import { Account, FileUpload } from "../API";
import { PATHS, toPath } from "../PATHS";
import { AccountModel } from "./accountStore";
import { IRootModel } from "./Root";
import { getAccessTokenForLambdaAuth } from "../utils/authHelpers";
import { customGetUser } from "../graphql/customQueries";
import * as CustomAPITypes from "../graphql/customAPI";

const getAccountQuery = `query GetAccount($id: ID!) {
  getAccount(id: $id) {
    id
    gid
    name
    fileuploads {
      originalName
      createdAt
      fileName
      fileStatus
      fileError
      numRecords
      numErrors
      totalRecords
      writtenRecords
      __typename
    }
    integrations {
      hubspot
      __typename
    }
    aliasId
    fonts {
      fontFamily
      __typename
    }
    minisiteDomain
    mfaEnabled
    users {
      nextToken
      __typename
    }
    createNewVideosWithBlingLogo
    accountType {
      levelName
      __typename
    }
    createdAt
    updatedAt
    __typename
  }
}
`;

export const UserModel = types
  .model({
    email: types.maybe(types.string),
    accounts: types.maybe(types.array(AccountModel)),
    mfaConfig: types.maybe(
      types.enumeration("MFAConfig", ["UNSET", "DISABLED", "ENABLED"])
    ),
    mfaEnabled: types.maybe(types.boolean),
    identityId: types.maybe(types.string),
    phoneNumber: types.maybe(types.maybeNull(types.string)),
    sub: types.maybe(types.string),
    loadStatus: types.optional(
      types.enumeration("LoadStatus", ["LOADING", "LOADED"]),
      "LOADING"
    ),
    token: types.maybe(types.maybeNull(types.string)),
    session: types.maybe(types.frozen()),
  })
  .views((self) => ({
    get mfaShouldBeEnabled() {
      if (self.mfaConfig === "ENABLED") {
        return true;
      }
      if (self.mfaConfig === "DISABLED") {
        return false;
      }
      return self.accounts?.some((account) => account.mfaEnabled) || false;
    },
    get isBlingsUser() {
      return self.email?.endsWith("@blings.io") || false;
    },
    get hasBlingsAccess() {
      return (
        self.accounts?.some((account) => account.id === "blings_account") ||
        false
      );
    },
    get mainAccount() {
      return (
        self.accounts?.find((account) => account.id === "blings_account") ||
        self.accounts?.[0]
      );
    },
    get hasMultipleAccounts() {
      return self?.accounts ? self?.accounts?.length > 1 : false;
    },
    accountFromId(id?: string) {
      if (!id) return undefined;
      return self.accounts?.find((account) => account.id === id);
    },
  }))
  .volatile((self) => ({
    loadCallbackFunctions: [] as (() => void)[],
  }))
  .actions((self) => ({
    addFunctionToLoadCallback(func: () => void) {
      if (self.loadStatus !== "LOADED") self.loadCallbackFunctions.push(func);
      else func();
    },
    async getCurrentAuthToken(): Promise<string> {
      try {
        const session = await fetchAuthSession();
        if (session && session.tokens && session.tokens.accessToken) {
          return session.tokens.accessToken.toString();
        }
        // Fallback to stored session if fetch fails
        return self.session.accessToken.toString() as string;
      } catch (error) {
        console.error("Error fetching current auth token:", error);
        // Fallback to stored session as last resort
        return self.session.accessToken.toString() as string;
      }
    },
    async signOut(history: NavigateFunction) {
      try {
        await signOutAndDeleteCookies(del);
        getRoot<IRootModel>(
          self
        ).projectsStore.removeProjectsFromLocalStorage();
        getRoot<IRootModel>(self).resetStore();
        history(toPath(PATHS.home));
        window.location.reload();
      } catch (e) {
        console.log(e, "failed to log out");
      }
    },
    load: flow(function* () {
      self.loadStatus = "LOADING";
      const promises = [
        fetchAuthSession(),
        fetchUserAttributes(),
        fetchMFAPreference(),
        getCurrentUser(),
      ];
      const authResult = yield Promise.all(promises);
      const fullAuth = authResult[0] as AuthSession;
      const attributes = authResult[1] as IUserAttributes;
      const { preferred, enabled } = authResult[2] as FetchMFAPreferenceOutput;
      const amplifyUser = authResult[3];
      const session = fullAuth.tokens;
      if (!session) throw new Error("No session");
      if (!session.idToken) throw new Error("No idToken");
      if (!attributes.email) throw new Error("No email");

      // Set basic user attributes
      self.sub = attributes.sub;
      self.email = attributes.email;
      self.phoneNumber = attributes.phone_number || null;
      self.identityId = fullAuth.identityId as string;
      self.session = session;
      self.mfaConfig =
        !attributes["custom:mfaConfig"] ||
        attributes["custom:mfaConfig"] === "0"
          ? "UNSET"
          : attributes["custom:mfaConfig"] === "1"
          ? "DISABLED"
          : "ENABLED";
      self.mfaEnabled = (enabled?.length || 0) > 0;

      // Get user with accounts from DynamoDB using customGetUser with pagination
      const client = generateClient();
      const allAccountUsers: Array<any> = [];
      let nextToken: string | null = null;

      do {
        const authToken = yield getAccessTokenForLambdaAuth();
        const response: { data: CustomAPITypes.CustomGetUserQuery } =
          yield client.graphql({
            query: customGetUser,
            variables: {
              id: amplifyUser.userId,
              accountsLimit: 100,
              ...(nextToken && { accountsNextToken: nextToken }),
            },
            authMode: "lambda",
            authToken,
          });

        const userData = response.data;
        if (!userData?.getUser) {
          throw new Error("User not found in database");
        }

        const accountUsers = userData.getUser.accounts?.items || [];
        allAccountUsers.push(
          ...accountUsers.filter(
            (item) => item?.account?.tierId === "ENTERPRISE"
          )
        );
        nextToken = userData.getUser.accounts?.nextToken || null;
      } while (nextToken);

      // Transform AccountUser data to AccountModel instances
      const accounts = allAccountUsers.map((accountUser) => {
        const account = accountUser.account!;

        type FontType = NonNullable<NonNullable<typeof account.fonts>[number]>;
        type VariantType = NonNullable<FontType["variants"][number]>;
        type FileUploadType = NonNullable<
          NonNullable<typeof account.fileuploads>[number]
        >;

        return AccountModel.create({
          id: account.id,
          name: account.name,
          createdAt: account.createdAt,
          updatedAt: account.updatedAt,
          aliasId: account.aliasId,
          minisiteDomain: account.minisiteDomain || "",
          accountType: account.accountType,
          fonts: account.fonts
            ? account.fonts
                .filter((f: FontType | null): f is FontType => f !== null)
                .map((f: FontType) => ({
                  fontFamily: f.fontFamily,
                  variants: f.variants
                    .filter(
                      (v: VariantType | null): v is VariantType => v !== null
                    )
                    .map((v: VariantType) => ({
                      name: v.name,
                      weight: v.weight,
                      style: v.style,
                      url: v.url,
                    })),
                }))
            : [],
          fileUploads: (account.fileuploads || []).filter(
            (f: FileUploadType | null): f is FileUploadType => f !== null
          ) as Array<FileUpload>,
          createNewVideosWithBlingLogo: account.createNewVideosWithBlingLogo,
          mfaEnabled: account.mfaEnabled,
        });
      });

      self.accounts = cast(accounts);

      self.loadStatus = "LOADED";
      self.loadCallbackFunctions.forEach((func) => func());
    }),
  }));
interface IUserAttributes {
  email: string;
  sub: string;
  phone_number: string | null;
  identityId: string;
  "custom:mfaConfig": string | null;
}
type RemoveTypename<T> = T extends Array<infer U>
  ? Array<RemoveTypename<U>>
  : T extends object
  ? {
      [K in keyof T as Exclude<K, "__typename">]: RemoveTypename<T[K]>;
    }
  : T;
const removeTypename = <T>(data: T): RemoveTypename<T> => {
  if (Array.isArray(data)) {
    return data.map((item) => removeTypename(item)) as RemoveTypename<T>;
  }

  if (data !== null && typeof data === "object") {
    const newObj: any = {};

    Object.entries(data).forEach(([key, value]) => {
      if (key !== "__typename") {
        newObj[key] = removeTypename(value);
      }
    });

    return newObj;
  }

  return data as RemoveTypename<T>;
};
export type IUserModel = Instance<typeof UserModel>;
